import { Component, computed, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { DataShare, TypeShare } from './share.type';
import { SettingService } from '@services/setting.service';
import { CardBinder, Page, UserBinder, Share as ShareM } from '@models/binder.model';
import { BinderService } from '@services/binder.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'lgb-share',
  standalone: false,
  templateUrl: './share.html',
  styleUrl: './share.css',
})
export class Share {
  //input/output
  readonly shareCallBack=input<() => void>(null);
  readonly cancelCallBack=input<() => void>(null);
  readonly _data=input<DataShare>(null, {alias: 'shareData'});
  readonly data=computed(() => this.getData());
  readonly type=input<TypeShare>(null, {alias: 'typeShare'});
  readonly typeElement=input.required<ElementType>();                 //type d'élement (indique si binder/page/card)
  readonly element=input.required<UserBinder|Page|CardBinder>();      //element à partager

  //services
  readonly settings=inject(SettingService);
  readonly binderS=inject(BinderService);

  //data
  readonly settingMessage=signal<boolean>(false);
  readonly share=signal<ShareM>(null);

  //ui
  readonly loading=signal(false);
  readonly _dialog=viewChild<ElementRef<HTMLDialogElement>>('modal');
  readonly _action=viewChild<ElementRef<HTMLElement>>('action');
  readonly dialog=computed(() => this._dialog()?.nativeElement);
  readonly action=computed(() => this._action()?.nativeElement);

  getData(){
    let data: DataShare=this._data();
    if(!data && this.element() && this.share()){
      switch(this.typeElement()){
        case 'binder':
          data=this.binderS.getShareBinderData(this.element().ident, this.share().ident);
          break;
        case 'page':
          data=this.binderS.getSharePageData(this.element().ident, this.share().ident);
          break;
        case 'card':
          data=this.binderS.getShareCardData(this.element().ident, this.share().ident);
          break;
        default:
          break;
      }
    }

    return data;
  }

  //ouvre la popup ou lance le partage si l'option ne plus afficher le message 
  openShare(){
    //si message, on affiche la boite de dialog
    if(this.settings.options().messageShare)
      this.dialog().showModal();
    else //sinon on crée le share directement
      this.makeShare();
  }

  //crée le share
  makeShare(){
    this.loading.set(true);

    //création du share
    let obs$: Observable<ShareM>;
    switch(this.typeElement()){
      case 'binder':
        obs$=this.binderS.shareBinder(this.element().ident);
        break;
      case 'page':
        obs$=this.binderS.sharePage(this.element().ident);
        break;
      case 'card':
        obs$=this.binderS.shareCard(this.element().ident);
        break;
      default:
        break;
    }

    obs$.subscribe({
      next: share => {
        this.share.set(share);
        this.dialog().close();
        
        //click sur le lien pour le partage
        this.loading.set(false);
        this.action().click();
      },
      error: e => this.loading.set(false)
    });    
  }

  //désactive le partage (utilisé en cas d'annulation)
  disableShare(){
    this.loading.set(true);
    this.binderS.disableShare(this.share().ident).subscribe(() => this.loading.set(false));
  }

  //lance le bouton de partage si typeShare n'est pas renseigné
  shareClick(){
    if(this.type()==null){ //si le navigateur support le share et qu'il n'y a pas de typeShare
      if(navigator.share){
        navigator.share(this.data())
          .then(() => {
            if(this.shareCallBack())
              this.shareCallBack()();
          })
          .catch(err => {
            // L’utilisateur a souvent "annulé" → AbortError : à ignorer
            if(err?.name!=='AbortError')
              this.disableShare();

              //callback d'annulation
              if(this.cancelCallBack())
                this.cancelCallBack()();
          });
      }
      else{
        navigator.clipboard?.writeText(`${this.data().text} ${this.data().url}`);
        alert('Lien copié 👍');
      }
    }
  }

  //partage sur une plateforme en particulier si typeShare est renseigné
  shareUrl(){
    const isIOS=/iPad|iPhone|iPod/.test(navigator.userAgent);
    const msg=encodeURIComponent(`${this.data()?.text} ${this.data()?.url}`);

    // Liens de secours
    switch(this.type()){
      case 'sms':
        return isIOS ? `sms:&body=${msg}` : `sms:?body=${msg}`;
      case 'whatsapp':
        return `https://wa.me/?text=${msg}`;
      case 'messenger':
        return `fb-messenger://share?link=${encodeURIComponent(this.data()?.url)}`;
      case 'mail':
        return `mailto:?subject=${encodeURIComponent(this.data()?.title)}&body=${msg}`;
      default:
        return null;
    }
  }
}

type ElementType='binder' | 'page' | 'card';